import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Rocket, Users, Brain, Heart, ArrowRight, EnvelopeSimple, User, ChatText } from '@phosphor-icons/react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore/lite';
import { db } from '../firebase';
import SparkLogo from '../components/SparkLogo';
import { useTranslation } from '../context/LanguageContext';
import { PageWrapper, Container, SectionTitle } from '../styles/shared';

const AboutContainer = styled.main`
  background: #0e1217;
  color: white;
  min-height: 100vh;
  width: 100%;
  padding-top: 64px; // For navbar
`;

const TimelineSection = styled.section`
  max-width: 1000px;
  margin: 0 auto;
  position: relative;
  padding: 2rem 1rem;
  background: #0e1217;

  &::before {
    content: '';
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    width: 2px;
    height: 100%;
    background: rgba(40, 192, 239, 0.2);
  }
`;

const StoryCard = styled(motion.div)`
  background: #161b22;
  border-radius: 16px;
  padding: 2rem;
  margin: 2rem 0;
  width: 80%;
  position: relative;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.2);

  &:nth-child(odd) {
    margin-right: auto;
    &::after {
      content: '';
      position: absolute;
      right: -10px;
      top: 30px;
      width: 20px;
      height: 20px;
      background: #28c0ef;
      border-radius: 50%;
    }
  }

  &:nth-child(even) {
    margin-left: auto;
    &::after {
      content: '';
      position: absolute;
      left: -10px;
      top: 30px;
      width: 20px;
      height: 20px;
      background: #28c0ef;
      border-radius: 50%;
    }
  }

  h2 {
    font-size: 2rem;
    margin-bottom: 1rem;
    color: #28c0ef;
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  p {
    font-size: 1.1rem;
    line-height: 1.6;
    color: rgba(255, 255, 255, 0.9);
  }
`;

const ComparisonPreview = styled(motion.div)`
  background: #161b22;
  border-radius: 16px;
  padding: 2rem;
  margin: 4rem auto;
  max-width: 1000px;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const ContactSection = styled(motion.div)`
  max-width: 450px;
  margin: 4rem auto;
  background: #161b22;
  border-radius: 24px;
  padding: 4rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.2);

  h2 {
    font-size: 2.8rem;
    color: #28c0ef;
    margin-bottom: 1rem;
  }

  p {
    color: rgba(255, 255, 255, 0.8);
    margin-bottom: 2.5rem;
    font-size: 1.2rem;
  }
`;

const ContactForm = styled.form`
  display: grid;
  gap: 1.8rem;
  width: 100%;
  padding: 0 1rem;
`;

const FormGroup = styled.div`
  position: relative;
  width: 100%;

  .icon {
    position: absolute;
    left: 1.2rem;
    top: 50%;
    transform: translateY(-50%);
    color: #28c0ef;
    opacity: 0.8;
  }
`;

const Input = styled.input`
  width: 90%;
  padding: 1rem 1rem 1rem 3rem;
  background: #1c2128;
  border: 1px solid #28c0ef;
  border-radius: 12px;
  color: white;
  font-size: 0.95rem;
  transition: all 0.2s;
  height: 2.8rem;
  margin: 0 auto;

  &:focus {
    outline: none;
    border-color: #28c0ef;
    background: #1c2128;
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.6);
  }
`;

const TextArea = styled.textarea`
  width: 90%;
  padding: 1rem 1rem 1rem 3rem;
  background: #1c2128;
  border: 1px solid #28c0ef;
  border-radius: 12px;
  color: white;
  font-size: 0.95rem;
  transition: all 0.2s;
  min-height: 120px;
  resize: vertical;
  margin: 0 auto;

  &:focus {
    outline: none;
    border-color: #28c0ef;
    background: #1c2128;
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.6);
  }
`;

const SubmitButton = styled.button`
  background: #28c0ef;
  color: white;
  padding: 1.2rem 2.5rem;
  border: none;
  border-radius: 12px;
  font-size: 1.1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  width: fit-content;
  margin-top: 1rem;

  &:hover {
    background: #1fa9d6;
    transform: translateY(-2px);
  }

  &:disabled {
    background: rgba(40, 192, 239, 0.5);
    cursor: not-allowed;
    transform: none;
  }
`;

const SuccessMessage = styled(motion.div)`
  color: #10B981;
  background: rgba(16, 185, 129, 0.1);
  border: 1px solid rgba(16, 185, 129, 0.2);
  padding: 1rem;
  border-radius: 12px;
  margin-top: 1rem;
  text-align: center;
`;

const ErrorMessage = styled(motion.div)`
  color: #EF4444;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.2);
  padding: 1rem;
  border-radius: 12px;
  margin-top: 1rem;
  text-align: center;
`;

const AboutPage: React.FC = () => {
  const { t } = useTranslation();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      await addDoc(collection(db, "contactForms"), {
        ...formData,
        source: "AboutPage",
        createdAt: serverTimestamp()
      });
      
      setFormData({ name: '', email: '', message: '' });
      setSubmitStatus('success');
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PageWrapper>
      <AboutContainer>
        <TimelineSection>
          <StoryCard
            initial={{ opacity: 0, x: -100 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2>
              <Rocket weight="fill" size={32} />
              Our Story
            </h2>
            <p>
              At SparkSkool, we understand that teachers today face many challenges. They often have to handle a lot of paperwork and other tasks, which means they work long hours and feel stressed. This extra work takes time away from teaching and can lead to burnout, causing many teachers, especially newcomers, to leave the profession. Students also suffer when teachers lack adequate support, impacting their learning and development.
            </p>
          </StoryCard>

          <StoryCard
            initial={{ opacity: 0, x: 100 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <h2>
              <Brain weight="fill" size={32} />
              About SparkSkool
            </h2>
            <p>
              To help with these problems, we created SparkSkool. Our mission is to bring artificial intelligence into every classroom and make education accessible, easy, and fun for everyone. Our platform uses smart tools to take care of repetitive tasks, giving teachers more time and energy to focus on their students.
            </p>
          </StoryCard>

          <StoryCard
            initial={{ opacity: 0, x: -100 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <h2>
              <Users weight="fill" size={32} />
              Meet the Team
            </h2>
            <p>
              Our team is made up of experienced teachers, AI experts, technologists, and creative thinkers who are all passionate about improving education. We combine our different skills to build solutions that are both advanced and easy to use, ensuring SparkSkool meets the real needs of teachers and students.
            </p>
          </StoryCard>

          <StoryCard
            initial={{ opacity: 0, x: 100 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.6 }}
          >
            <h2>
              <Heart weight="fill" size={32} />
              Join Us
            </h2>
            <p>
              We invite you to be part of the change. Help us shape the future of education with AI.
            </p>
          </StoryCard>
        </TimelineSection>

        <ContactSection
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <h2>Get in Touch</h2>
          <p>Have questions? We'd love to hear from you.</p>
          
          <ContactForm onSubmit={handleSubmit}>
            <FormGroup>
              <User className="icon" size={20} />
              <Input
                type="text"
                name="name"
                placeholder="Your Name"
                value={formData.name}
                onChange={handleInputChange}
                required
              />
            </FormGroup>

            <FormGroup>
              <EnvelopeSimple className="icon" size={20} />
              <Input
                type="email"
                name="email"
                placeholder="Your Email"
                value={formData.email}
                onChange={handleInputChange}
                required
              />
            </FormGroup>

            <FormGroup>
              <ChatText className="icon" size={20} />
              <TextArea
                name="message"
                placeholder="Your Message"
                value={formData.message}
                onChange={handleInputChange}
                required
              />
            </FormGroup>

            <SubmitButton type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Sending...' : 'Send Message'} {!isSubmitting && <ArrowRight weight="bold" />}
            </SubmitButton>

            {submitStatus === 'success' && (
              <SuccessMessage
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                Thank you for your message! We'll get back to you soon.
              </SuccessMessage>
            )}

            {submitStatus === 'error' && (
              <ErrorMessage
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                There was an error sending your message. Please try again.
              </ErrorMessage>
            )}
          </ContactForm>
        </ContactSection>
      </AboutContainer>

    </PageWrapper>
  );
};

export default AboutPage;